import { useState, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { createService, uploadServiceImages } from "apis";
import { useAppModal } from "components/common/AppModalProvider";
import { compressImage } from "utils/imageCompressor";
import { ServiceCategory } from "types";

interface ScheduleInput {
  id: number;
  date: string;
  startTime: string;
  endTime: string;
}

interface ImageItem {
  id: number;
  file: File;
  preview: string;
  isMain: boolean;
}

let scheduleIdCounter = 0;
let imageIdCounter = 0;

const categories: { icon: string; label: string; value: ServiceCategory }[] = [
  { icon: "💬", label: "수다·티타임", value: ServiceCategory.CHAT },
  { icon: "🎮", label: "게임·듀오", value: ServiceCategory.GAME },
  { icon: "🔮", label: "타로·운세", value: ServiceCategory.TAROT },
  { icon: "💡", label: "고민·피드백", value: ServiceCategory.FEEDBACK },
  { icon: "💻", label: "랜선 스터디", value: ServiceCategory.STUDY },
  { icon: "✨", label: "자유주제", value: ServiceCategory.FREE },
];

const categoryPlaceholders: Record<ServiceCategory, { title: string; description: string }> = {
  [ServiceCategory.CHAT]: {
    title: "예: 심심할 때 같이 수다 떨어요 ☕",
    description: "예: 밥 먹으면서 같이 통화해요! 외로울 때, 심심할 때 편하게 연락주세요. 랜선 술자리도 환영!",
  },
  [ServiceCategory.GAME]: {
    title: "예: 롤 듀오 같이 올라가요 🎮",
    description: "예: 골드~플래 서포터 장인입니다. 듀오 같이 올라가실 분! 발로란트, 오버워치도 가능해요.",
  },
  [ServiceCategory.TAROT]: {
    title: "예: 연애운 타로 봐드려요 🔮",
    description: "예: 3년차 타로 리더입니다. 연애, 진로, 오늘의 운세 등 가볍게 15분~30분 봐드려요.",
  },
  [ServiceCategory.FEEDBACK]: {
    title: "예: 소개팅 프사 골라드림 💡",
    description: "예: 센스 있는 피드백 해드려요! 이력서 첨삭, 카톡 분석, 코디 조언 등 캐주얼 상담.",
  },
  [ServiceCategory.STUDY]: {
    title: "예: 같이 공부해요 (캠스터디) 💻",
    description: "예: 줌 켜놓고 각자 할 일 하는 러닝메이트! 공부, 업무, 독서 다 좋아요.",
  },
  [ServiceCategory.FREE]: {
    title: "예: 노래 불러드림 🎤",
    description: "예: 자장가 ASMR, 아재개그 30분, 고민 들어드림 등 자유롭게 등록해보세요!",
  },
};

const HostServiceForm = () => {
  const navigate = useNavigate();
  const { showAlert } = useAppModal();
  const [step, setStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const imageInputRef = useRef<HTMLInputElement>(null);

  // Step 1
  const [category, setCategory] = useState<ServiceCategory>(ServiceCategory.CHAT);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [images, setImages] = useState<ImageItem[]>([]);

  // Step 2
  const [price, setPrice] = useState<number>(0);
  const [durationMinutes, setDurationMinutes] = useState<number>(60);
  const [schedules, setSchedules] = useState<ScheduleInput[]>([]);

  // Step 3
  const [externalLink, setExternalLink] = useState("");

  // ─── Image Handlers ───
  const handleImageSelect = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const rawFiles = Array.from(e.target.files || []);
    // Compress all images before adding
    const compressedFiles = await Promise.all(rawFiles.map(f => compressImage(f)));
    const newImages: ImageItem[] = compressedFiles.map(file => ({
      id: ++imageIdCounter,
      file,
      preview: URL.createObjectURL(file),
      isMain: images.length === 0 && imageIdCounter === 1,
    }));
    setImages(prev => {
      const updated = [...prev, ...newImages].slice(0, 10); // max 10 images
      // If no main photo set, first one is main
      if (!updated.some(img => img.isMain) && updated.length > 0) {
        updated[0].isMain = true;
      }
      return updated;
    });
    if (imageInputRef.current) imageInputRef.current.value = "";
  }, [images.length]);

  const removeImage = useCallback((id: number) => {
    setImages(prev => {
      const filtered = prev.filter(img => img.id !== id);
      // If removed image was main, set first remaining as main
      if (filtered.length > 0 && !filtered.some(img => img.isMain)) {
        filtered[0].isMain = true;
      }
      return filtered;
    });
  }, []);

  const setMainImage = useCallback((id: number) => {
    setImages(prev => prev.map(img => ({ ...img, isMain: img.id === id })));
  }, []);

  const moveImage = useCallback((id: number, direction: 'left' | 'right') => {
    setImages(prev => {
      const idx = prev.findIndex(img => img.id === id);
      if ((direction === 'left' && idx === 0) || (direction === 'right' && idx === prev.length - 1)) return prev;
      const newArr = [...prev];
      const swapIdx = direction === 'left' ? idx - 1 : idx + 1;
      [newArr[idx], newArr[swapIdx]] = [newArr[swapIdx], newArr[idx]];
      return newArr;
    });
  }, []);

  // Validation
  const step1Valid = category && title.trim().length > 0 && description.trim().length > 0;
  const step2Valid = price > 0 && schedules.length > 0 && schedules.every(s => s.date && s.startTime && s.endTime);

  // Schedule overlap helper
  const checkOverlap = useCallback((date: string, startTime: string, endTime: string, excludeId?: number): boolean => {
    const toMin = (t: string) => { const [h, m] = t.split(':').map(Number); return h * 60 + m; };
    const newStart = toMin(startTime);
    const newEnd = toMin(endTime);
    return schedules.some(s => {
      if (s.date !== date) return false;
      if (excludeId !== undefined && s.id === excludeId) return false;
      const sStart = toMin(s.startTime);
      const sEnd = toMin(s.endTime);
      return newStart < sEnd && newEnd > sStart;
    });
  }, [schedules]);
  const step3Valid = externalLink.trim().length > 0;

  const addSchedule = () => {
    setSchedules(prev => [...prev, { id: ++scheduleIdCounter, date: "", startTime: "", endTime: "" }]);
  };
  const removeSchedule = (id: number) => setSchedules(prev => prev.filter(s => s.id !== id));
  const updateSchedule = (id: number, field: keyof Omit<ScheduleInput, "id">, value: string) => {
    setSchedules(prev => prev.map(s => (s.id === id ? { ...s, [field]: value } : s)));
  };

  const handleSubmit = async () => {
    if (!step1Valid || !step2Valid || !step3Valid) return;
    setIsSubmitting(true);
    try {
      // 1. Upload images if any
      let imagePaths: string[] = [];
      if (images.length > 0) {
        // Sort so main image is first
        const sortedImages = [...images].sort((a, b) => (b.isMain ? 1 : 0) - (a.isMain ? 1 : 0));
        imagePaths = await uploadServiceImages(sortedImages.map(img => img.file));
      }

      // 2. Create service with image paths
      await createService(title, description, price, category, externalLink, durationMinutes,
        schedules.map(({ date, startTime, endTime }) => ({ date, startTime, endTime })),
        imagePaths
      );
      showAlert("등록 완료! 🎉", { variant: 'success', icon: '🎉' });
      navigate("/service");
    } catch (error: any) {
      console.error(error);
      const msg = error?.response?.data?.message || error?.message || "등록 중 오류가 발생했습니다.";
      showAlert(typeof msg === 'string' ? msg : JSON.stringify(msg), { variant: 'error' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const stepIndicator = (
    <div className="flex items-center justify-center gap-2 py-5">
      {[1, 2, 3].map(n => (
        <div key={n} className="flex items-center gap-2">
          <div className={`w-8 h-8 rounded-full flex items-center justify-center text-[12px] font-bold transition-all duration-300
            ${step === n ? 'bg-primary-500 text-white scale-110 shadow-md shadow-primary-500/30' :
              step > n ? 'bg-primary-100 text-primary-500' : 'bg-achromatic-100 text-achromatic-400'}`}>
            {step > n ? '✓' : n}
          </div>
          {n < 3 && <div className={`w-8 h-0.5 rounded transition-colors ${step > n ? 'bg-primary-300' : 'bg-achromatic-100'}`} />}
        </div>
      ))}
    </div>
  );

  return (
    <div className="animate-fadeUp mobile:pb-[56px]">
      {/* Header */}
      <div className="flex items-center gap-3 px-5 py-4">
        <button onClick={() => step > 1 ? setStep(step - 1) : navigate(-1)}
          className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-achromatic-100 transition-colors">
          ←
        </button>
        <h1 className="text-lg font-extrabold text-achromatic-900">내 시간 등록하기</h1>
      </div>

      {stepIndicator}

      <div className="bg-white rounded-2xl overflow-hidden mobile:rounded-none mx-4 mobile:mx-0">
        {/* ── Step 1: What ── */}
        {step === 1 && (
          <div className="flex flex-col gap-6 p-6">
            <div>
              <label className="text-[12px] font-bold text-achromatic-500 uppercase tracking-wider">카테고리</label>
              <div className="grid grid-cols-3 gap-2 mt-2">
                {categories.map(cat => (
                  <button key={cat.value} onClick={() => setCategory(cat.value)}
                    className={`flex flex-col items-center gap-1.5 py-4 rounded-xl border transition-all duration-200
                      ${category === cat.value
                        ? 'border-primary-500 bg-primary-50 shadow-sm shadow-primary-500/10'
                        : 'border-achromatic-100 hover:border-achromatic-200'}`}>
                    <span className="text-2xl">{cat.icon}</span>
                    <span className={`text-[12px] font-bold ${category === cat.value ? 'text-primary-600' : 'text-achromatic-600'}`}>
                      {cat.label}
                    </span>
                  </button>
                ))}
              </div>
            </div>

            <div className="flex flex-col gap-1">
              <label className="text-[12px] font-bold text-achromatic-500 uppercase tracking-wider">한 줄 제목</label>
              <input type="text" value={title} onChange={e => setTitle(e.target.value)}
                placeholder={categoryPlaceholders[category].title}
                maxLength={40}
                className="border border-achromatic-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-primary-500 transition-colors" />
              <span className="text-[11px] text-achromatic-400 text-right">{title.length}/40</span>
            </div>

            <div className="flex flex-col gap-1">
              <label className="text-[12px] font-bold text-achromatic-500 uppercase tracking-wider">설명</label>
              <textarea value={description} onChange={e => setDescription(e.target.value)}
                placeholder={categoryPlaceholders[category].description}
                rows={3} maxLength={200}
                className="border border-achromatic-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-primary-500 transition-colors resize-none" />
              <span className="text-[11px] text-achromatic-400 text-right">{description.length}/200</span>
            </div>

            {/* ── Image Upload (Danggeun-style) ── */}
            <div className="flex flex-col gap-2">
              <div className="flex justify-between items-center">
                <label className="text-[12px] font-bold text-achromatic-500 uppercase tracking-wider">
                  사진 ({images.length}/10)
                </label>
                <span className="text-[11px] text-achromatic-400">첫 번째 사진이 대표 이미지</span>
              </div>
              <div className="flex gap-2 overflow-x-auto pb-2">
                {/* Add button */}
                <button
                  onClick={() => imageInputRef.current?.click()}
                  className="flex-shrink-0 w-[88px] h-[88px] rounded-xl border-2 border-dashed border-achromatic-200
                    flex flex-col items-center justify-center gap-1
                    hover:border-primary-400 hover:bg-primary-50/30 transition-all cursor-pointer"
                >
                  <span className="text-2xl text-achromatic-300">📷</span>
                  <span className="text-[10px] font-bold text-achromatic-400">{images.length}/10</span>
                </button>
                <input ref={imageInputRef} type="file" accept="image/*" multiple className="hidden" onChange={handleImageSelect} />

                {/* Image previews */}
                {images.map((img, idx) => (
                  <div key={img.id} className="flex-shrink-0 relative group">
                    <div className={`w-[88px] h-[88px] rounded-xl overflow-hidden border-2 transition-colors
                      ${img.isMain ? 'border-primary-500' : 'border-achromatic-100'}`}>
                      <img src={img.preview} alt="" className="w-full h-full object-cover" />
                    </div>
                    {/* Main badge */}
                    {img.isMain && (
                      <span className="absolute top-1 left-1 bg-primary-500 text-white text-[9px] font-bold px-1.5 py-0.5 rounded">대표</span>
                    )}
                    {/* Hover controls */}
                    <div className="absolute inset-0 bg-black/40 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-1">
                      {!img.isMain && (
                        <button onClick={() => setMainImage(img.id)} className="w-6 h-6 bg-white rounded-full flex items-center justify-center text-[10px] hover:bg-primary-50" title="대표 사진 설정">⭐</button>
                      )}
                      {idx > 0 && (
                        <button onClick={() => moveImage(img.id, 'left')} className="w-6 h-6 bg-white rounded-full flex items-center justify-center text-[10px] hover:bg-primary-50" title="왼쪽으로">←</button>
                      )}
                      {idx < images.length - 1 && (
                        <button onClick={() => moveImage(img.id, 'right')} className="w-6 h-6 bg-white rounded-full flex items-center justify-center text-[10px] hover:bg-primary-50" title="오른쪽으로">→</button>
                      )}
                      <button onClick={() => removeImage(img.id)} className="w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center text-[10px] hover:bg-red-600" title="삭제">✕</button>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <button onClick={() => setStep(2)} disabled={!step1Valid}
              className={`w-full py-3.5 rounded-xl font-bold text-sm transition-all duration-200
                ${step1Valid
                  ? 'bg-primary-500 text-white hover:bg-primary-600 active:scale-[0.98]'
                  : 'bg-achromatic-100 text-achromatic-400 cursor-not-allowed'}`}>
              다음 →
            </button>
          </div>
        )}

        {/* ── Step 2: When & Price ── */}
        {step === 2 && (
          <div className="flex flex-col gap-6 p-6">
            <div className="grid grid-cols-2 gap-4">
              <div className="flex flex-col gap-1">
                <label className="text-[12px] font-bold text-achromatic-500 uppercase tracking-wider">1회 가격 (원)</label>
                <input type="number" value={price || ""} onChange={e => setPrice(parseInt(e.target.value) || 0)}
                  placeholder="10,000" min="0"
                  className="border border-achromatic-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-primary-500 transition-colors" />
              </div>
              <div className="flex flex-col gap-1">
                <label className="text-[12px] font-bold text-achromatic-500 uppercase tracking-wider">세션 시간</label>
                <div className="flex gap-1.5 mb-1">
                  {[30, 60, 90, 120].map(m => (
                    <button key={m} onClick={() => setDurationMinutes(m)}
                      className={`flex-1 py-2.5 rounded-xl text-[12px] font-bold border transition-all
                        ${durationMinutes === m
                          ? 'border-primary-500 bg-primary-50 text-primary-600'
                          : 'border-achromatic-100 text-achromatic-500 hover:border-achromatic-200'}`}>
                      {m}분
                    </button>
                  ))}
                </div>
                <div className="flex items-center gap-2">
                  <button onClick={() => setDurationMinutes(prev => Math.max(10, prev - 10))}
                    className="w-9 h-9 rounded-lg border border-achromatic-200 flex items-center justify-center text-lg font-bold text-achromatic-500 hover:bg-achromatic-50 transition-colors">−</button>
                  <div className="flex-1 text-center py-2 bg-achromatic-50 rounded-lg">
                    <span className="text-[14px] font-bold text-achromatic-800">{durationMinutes}분</span>
                    <span className="text-[10px] text-achromatic-400 ml-1">({Math.floor(durationMinutes / 60)}시간 {durationMinutes % 60}분)</span>
                  </div>
                  <button onClick={() => setDurationMinutes(prev => Math.min(300, prev + 10))}
                    className="w-9 h-9 rounded-lg border border-achromatic-200 flex items-center justify-center text-lg font-bold text-achromatic-500 hover:bg-achromatic-50 transition-colors">+</button>
                </div>
              </div>
            </div>

            <div className="flex flex-col gap-2">
              <div className="flex justify-between items-center">
                <label className="text-[12px] font-bold text-achromatic-500 uppercase tracking-wider">
                  판매 가능 시간대 ({schedules.length}개)
                </label>
              </div>

              {/* Quick date selection */}
              <div className="flex flex-col gap-3">
                <p className="text-[11px] text-achromatic-400">날짜를 선택하면 시간대가 추가됩니다 (같은 날 여러 시간 가능)</p>
                <div className="flex gap-1.5 overflow-x-auto pb-1 scrollbar-hide">
                  {Array.from({ length: 14 }, (_, i) => {
                    const d = new Date();
                    d.setDate(d.getDate() + i);
                    const yyyy = d.getFullYear();
                    const mm = String(d.getMonth() + 1).padStart(2, '0');
                    const dd = String(d.getDate()).padStart(2, '0');
                    const dateStr = `${yyyy}-${mm}-${dd}`;
                    const dayNames = ['일', '월', '화', '수', '목', '금', '토'];
                    const dayName = dayNames[d.getDay()];
                    const month = d.getMonth() + 1;
                    const day = d.getDate();
                    const slotsForDay = schedules.filter(s => s.date === dateStr).length;
                    return (
                      <button
                        key={`${dateStr}-${i}`}
                        onClick={() => {
                          // Find a non-overlapping start time for this date
                          const existingSlots = schedules.filter(s => s.date === dateStr);
                          let hour = 10;
                          let minute = 0;
                          // Try to find an open slot
                          while (hour < 24) {
                            const startTime = `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
                            const endMinutes = hour * 60 + minute + durationMinutes;
                            const endTime = `${String(Math.floor(endMinutes / 60) % 24).padStart(2, '0')}:${String(endMinutes % 60).padStart(2, '0')}`;
                            if (!checkOverlap(dateStr, startTime, endTime)) {
                              setSchedules(prev => [...prev, { id: ++scheduleIdCounter, date: dateStr, startTime, endTime }]);
                              return;
                            }
                            // Move forward by durationMinutes
                            minute += durationMinutes;
                            if (minute >= 60) { hour += Math.floor(minute / 60); minute = minute % 60; }
                          }
                          showAlert(`${month}/${day}에 더 이상 추가할 수 있는 시간대가 없습니다.`, { variant: 'warning' });
                        }}
                        className={`flex-shrink-0 flex flex-col items-center gap-0.5 px-3 py-2 rounded-xl text-center transition-all min-w-[52px] relative ${
                          slotsForDay > 0
                            ? 'bg-primary-500 text-white shadow-sm'
                            : i === 0 ? 'bg-primary-50 text-primary-600 border border-primary-200'
                            : 'bg-achromatic-50 text-achromatic-600 hover:bg-achromatic-100'
                        }`}
                      >
                        <span className="text-[10px] font-medium">{month}월</span>
                        <span className="text-[16px] font-bold">{day}</span>
                        <span className={`text-[10px] font-medium ${slotsForDay > 0 ? '' : d.getDay() === 0 ? 'text-red-400' : d.getDay() === 6 ? 'text-blue-400' : ''}`}>{dayName}</span>
                        {slotsForDay > 0 && (
                          <span className="absolute -top-1 -right-1 w-4 h-4 bg-white text-primary-500 rounded-full text-[9px] font-bold flex items-center justify-center shadow-sm">
                            {slotsForDay}
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Added schedules — each with direct time input */}
              {schedules.length > 0 && (
                <div className="flex flex-col gap-2 max-h-[400px] overflow-y-auto">
                  {schedules.map(s => {
                    const [y, m, dayNum] = s.date.split('-').map(Number);
                    const dateObj = new Date(y, m - 1, dayNum);
                    const dayNames = ['일', '월', '화', '수', '목', '금', '토'];
                    return (
                      <div key={s.id} className="bg-achromatic-50 rounded-xl p-3">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-[13px] font-bold text-achromatic-800">
                            {m}/{dayNum} ({dayNames[dateObj.getDay()]})
                          </span>
                          <button onClick={() => removeSchedule(s.id)}
                            className="text-red-400 hover:text-red-600 transition-colors text-sm font-bold">✕</button>
                        </div>
                        {/* Time selector — hour + minute dropdowns */}
                        <div className="flex items-center gap-2 flex-wrap">
                          <div className="flex items-center gap-1 bg-white rounded-lg border border-achromatic-200 px-2 py-1.5">
                            <select
                              value={parseInt(s.startTime.split(':')[0])}
                              onChange={e => {
                                const h = parseInt(e.target.value);
                                const mn = parseInt(s.startTime.split(':')[1]) || 0;
                                const startTime = `${String(h).padStart(2, '0')}:${String(mn).padStart(2, '0')}`;
                                const endMin = h * 60 + mn + durationMinutes;
                                const endTime = `${String(Math.floor(endMin / 60) % 24).padStart(2, '0')}:${String(endMin % 60).padStart(2, '0')}`;
                                if (checkOverlap(s.date, startTime, endTime, s.id)) {
                                  showAlert('해당 시간에 이미 다른 일정이 있습니다.', { variant: 'warning' });
                                  return;
                                }
                                updateSchedule(s.id, "startTime", startTime);
                                updateSchedule(s.id, "endTime", endTime);
                              }}
                              className="text-[14px] font-bold text-achromatic-800 bg-transparent focus:outline-none cursor-pointer appearance-none pr-1"
                            >
                              {Array.from({ length: 24 }, (_, i) => (
                                <option key={i} value={i}>{i}시</option>
                              ))}
                            </select>
                            <span className="text-achromatic-400 font-bold">:</span>
                            <select
                              value={parseInt(s.startTime.split(':')[1]) || 0}
                              onChange={e => {
                                const h = parseInt(s.startTime.split(':')[0]);
                                const mn = parseInt(e.target.value);
                                const startTime = `${String(h).padStart(2, '0')}:${String(mn).padStart(2, '0')}`;
                                const endMin = h * 60 + mn + durationMinutes;
                                const endTime = `${String(Math.floor(endMin / 60) % 24).padStart(2, '0')}:${String(endMin % 60).padStart(2, '0')}`;
                                if (checkOverlap(s.date, startTime, endTime, s.id)) {
                                  showAlert('해당 시간에 이미 다른 일정이 있습니다.', { variant: 'warning' });
                                  return;
                                }
                                updateSchedule(s.id, "startTime", startTime);
                                updateSchedule(s.id, "endTime", endTime);
                              }}
                              className="text-[14px] font-bold text-achromatic-800 bg-transparent focus:outline-none cursor-pointer appearance-none pr-1"
                            >
                              {Array.from({ length: 12 }, (_, i) => i * 5).map(m => (
                                <option key={m} value={m}>{String(m).padStart(2, '0')}분</option>
                              ))}
                            </select>
                          </div>
                          <span className="text-achromatic-400 text-sm">→</span>
                          <span className="text-[14px] font-bold text-primary-500 bg-primary-50 px-3 py-1.5 rounded-lg">
                            {s.endTime}
                          </span>
                          <span className="text-[11px] text-achromatic-400">({durationMinutes}분)</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {schedules.length === 0 && (
                <div className="flex flex-col items-center gap-2 py-6 text-achromatic-300">
                  <span className="text-2xl">📅</span>
                  <span className="text-[12px] font-medium">위에서 날짜를 선택해주세요</span>
                </div>
              )}
            </div>

            <button onClick={() => setStep(3)} disabled={!step2Valid}
              className={`w-full py-3.5 rounded-xl font-bold text-sm transition-all duration-200
                ${step2Valid
                  ? 'bg-primary-500 text-white hover:bg-primary-600 active:scale-[0.98]'
                  : 'bg-achromatic-100 text-achromatic-400 cursor-not-allowed'}`}>
              다음 →
            </button>
          </div>
        )}

        {/* ── Step 3: Where ── */}
        {step === 3 && (
          <div className="flex flex-col gap-6 p-6">
            <div className="bg-primary-50 rounded-xl p-4 border border-primary-100">
              <p className="text-[13px] text-primary-700 font-medium leading-relaxed">
                🔒 아래 링크는 <strong>결제 완료한 구매자에게만</strong> 공개됩니다.
                Zoom, 디스코드, Google Meet, 카카오 오픈채팅 등 서비스 진행 시 사용할 링크를 입력해주세요.
              </p>
            </div>

            <div className="flex flex-col gap-3">
              <label className="text-[12px] font-bold text-achromatic-500 uppercase tracking-wider">접속 링크 *</label>
              <div className="flex flex-col gap-2">
                {[
                  { icon: "📹", label: "Zoom", placeholder: "https://zoom.us/j/..." },
                  { icon: "🎮", label: "Discord", placeholder: "https://discord.gg/..." },
                  { icon: "📞", label: "Google Meet", placeholder: "https://meet.google.com/..." },
                  { icon: "💬", label: "카카오 오픈채팅", placeholder: "https://open.kakao.com/..." },
                ].map(opt => (
                  <button key={opt.label} onClick={() => { if (!externalLink) setExternalLink(opt.placeholder.split("...")[0]) }}
                    className="flex items-center gap-3 p-3 border border-achromatic-100 rounded-xl
                      hover:border-primary-300 hover:bg-primary-50/30 transition-all text-left">
                    <span className="text-lg">{opt.icon}</span>
                    <div>
                      <div className="text-[12px] font-bold text-achromatic-700">{opt.label}</div>
                      <div className="text-[11px] text-achromatic-400">{opt.placeholder}</div>
                    </div>
                  </button>
                ))}
              </div>
              <input type="url" value={externalLink} onChange={e => setExternalLink(e.target.value)}
                placeholder="링크를 입력하세요"
                className="border border-achromatic-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-primary-500 transition-colors" />
            </div>

            {/* Summary */}
            <div className="bg-achromatic-50 rounded-xl p-4">
              <div className="text-[12px] font-bold text-achromatic-500 uppercase tracking-wider mb-3">등록 요약</div>
              <div className="flex flex-col gap-1.5 text-[13px]">
                <div className="flex justify-between">
                  <span className="text-achromatic-400">카테고리</span>
                  <span className="font-medium text-achromatic-700">{categories.find(c => c.value === category)?.icon} {categories.find(c => c.value === category)?.label}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-achromatic-400">제목</span>
                  <span className="font-medium text-achromatic-700">{title}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-achromatic-400">가격</span>
                  <span className="font-bold text-achromatic-900">{price.toLocaleString()}원 / {durationMinutes}분</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-achromatic-400">시간대</span>
                  <span className="font-medium text-achromatic-700">{schedules.length}개</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-achromatic-400">사진</span>
                  <span className="font-medium text-achromatic-700">{images.length}장</span>
                </div>
              </div>
            </div>

            <button onClick={handleSubmit} disabled={!step3Valid || isSubmitting}
              className={`w-full py-3.5 rounded-xl font-bold text-sm transition-all duration-200
                ${step3Valid && !isSubmitting
                  ? 'bg-gradient-to-r from-primary-500 to-violet-500 text-white hover:shadow-lg hover:shadow-primary-500/30 active:scale-[0.98]'
                  : 'bg-achromatic-100 text-achromatic-400 cursor-not-allowed'}`}>
              {isSubmitting ? "등록 중..." : "🎉 등록 완료!"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default HostServiceForm;
